import * as SubframeCore from '@subframe/core';
import { useState } from 'react';
import { Breadcrumbs } from 'subframe/index';
import { Tabs } from 'subframe/components/Tabs';
import useUserAccountState from 'hooks/useUserAccountState';
import { useGetAccountTeam, useListTeamInvitations } from 'api/frontend';
import TeamsMembers from './components/TeamMembers';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import TeamsPendingMembers from './components/TeamPendingMembers';
import Page from 'components/Page';
import { Alert, SkeletonText } from 'subframe/index';
import { InviteTeamMemberDialog } from 'components/dialogs';
import useDialog from 'hooks/useDialog';
import { CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useStatsigClient } from '@statsig/react-bindings';
import { InviteTeamAwsIdentityDialog } from './components/InviteTeamAwsIdentityDialog';
import { Actions } from 'components/design-system';
import TeamsCloudIdentities from './components/TeamCloudIdentities';

function Teams() {
  const { account } = useUserAccountState();
  const { logEvent } = AnalyticsEventLogger();
  const { setDialogType, currentDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();
  const { client: StatsigClient } = useStatsigClient();
  const showSsoBanner = StatsigClient.checkGate('teams_sso_banner');

  type TabTypes = 'members' | 'pending';

  const {
    data: membersListToShow,
    isValidating,
    mutate: refetchMembers,
  } = useGetAccountTeam('default', 'default', {
    request: { headers: { Authorization: `Bearer ${account.token}` } },
  });

  const {
    data: pendingMembersList,
    isValidating: validatingPendingMembers,
    mutate: refetchPendingMembers,
  } = useListTeamInvitations(
    'default',
    'default',
    {},
    {
      request: { headers: { Authorization: `Bearer ${account.token}` } },
    },
  );

  const [currentTab, setCurrentTab] = useState<TabTypes>('members');
  const isCurrentTabLoading =
    currentTab === 'members'
      ? isValidating && !membersListToShow
      : validatingPendingMembers && !pendingMembersList;

  const peopleList = membersListToShow?.members?.filter((e) => e.user_id);
  const cloudIdentitiesList = membersListToShow?.members?.filter(
    (e) => e.user_id === undefined,
  );

  const TEAM_TABS = [
    {
      key: 'members',
      id: 'members-tab',
      title: `Members ${
        membersListToShow ? `(${membersListToShow?.members?.length})` : ''
      }`,
      icon: 'FeatherUsers' as SubframeCore.IconName,
      trackerEvent: 'team-members-tab',
      component: !isCurrentTabLoading && (
        <TeamsMembers
          members={membersListToShow?.members || []}
          refetchMembers={refetchMembers}
        />
      ),
    },
    {
      key: 'people',
      id: 'people-tab',
      title: `People ${peopleList ? `(${peopleList.length})` : ''}`,
      icon: 'FeatherUser',
      trackerEvent: 'team-people-tab',
      component: (
        <TeamsMembers
          members={peopleList || []}
          refetchMembers={refetchMembers}
        />
      ),
    },
    {
      key: 'cloud-identities',
      id: 'cloud-identities-tab',
      title: `Cloud Identities ${
        cloudIdentitiesList ? `(${cloudIdentitiesList?.length})` : ''
      }`,
      icon: 'FeatherCloud',
      trackerEvent: 'team-cloud-identities-tab',
      component: (
        <TeamsCloudIdentities
          members={cloudIdentitiesList || []}
          refetchMembers={refetchMembers}
        />
      ),
    },
    {
      key: 'pending',
      id: 'pending-members-tab',
      title: `Pending (${pendingMembersList?.data?.length || 0})`,
      icon: 'FeatherUserMinus2',
      trackerEvent: 'team-pending-tab',
      component: !isCurrentTabLoading && (
        <TeamsPendingMembers
          pendingMembers={pendingMembersList?.data || []}
          refetchPendingMembers={refetchPendingMembers}
        />
      ),
    },
  ];
  return (
    <Page title="Teams">
      <InviteTeamMemberDialog
        handleOpenChange={(open) => {
          if (!open) {
            refetchMembers();
            refetchPendingMembers();
            setDialogType('');
          }
        }}
        open={currentDialog === 'invite-member-to-team'}
      />
      <InviteTeamAwsIdentityDialog
        handleOpenChange={(open) => {
          if (!open) {
            refetchMembers();
            refetchPendingMembers();
            setDialogType('');
          }
        }}
        open={currentDialog === 'invite-aws-identity-to-team'}
      />
      <div className="h-full flex-col gap-6 items-start flex pl-0 pr-3 pt-8 pb-0 bg-default-background ml-8 w-[calc(100%-64px)]">
        <div className="w-full flex-col items-start flex">
          <div className="w-full flex-col gap-6 items-start flex pl-3 pr-0 py-0">
            <div className="w-full flex-col gap-6 items-start flex">
              <Breadcrumbs>
                <Breadcrumbs.Item>Configure</Breadcrumbs.Item>
                <Breadcrumbs.Divider />
                <Breadcrumbs.Item active={true}>Teams</Breadcrumbs.Item>
              </Breadcrumbs>
              <div className="w-full flex-col gap-2 items-start flex">
                <div className="flex pl-0 pr-0 py-0 justify-between items-start self-stretch">
                  <div className="w-full flex-col gap-2 items-start flex">
                    <span className="text-default-font text-section-header">
                      Teams
                    </span>
                    <span className="text-subtext-color text-label">
                      Chkk organizes users in Teams to manage permissions
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Actions
                      size="large"
                      sections={[
                        {
                          title: 'People',
                          items: [
                            <Actions.Item
                              key="invite-team-member"
                              title="Invite Team Member"
                              icon="FeatherUserPlus"
                              onClick={() => {
                                setDialogType('invite-member-to-team');
                                logEvent('invite-new-member');
                              }}
                            />,
                          ],
                        },
                        {
                          title: 'Cloud Identities',
                          items: [
                            <Actions.Item
                              key="invite-aws-identity"
                              title="Add AWS Identity"
                              icon="FeatherUploadCloud"
                              onClick={() => {
                                setDialogType('invite-aws-identity-to-team');
                                logEvent('invite-new-aws-identity');
                              }}
                            />,
                            <Actions.Item
                              key="invite-gcp-identity"
                              title="Add GCP Identity"
                              icon="FeatherUploadCloud"
                              disabled={true}
                            />,
                            <Actions.Item
                              key="invite-azure-identity"
                              title="Add Azure Identity"
                              icon="FeatherUploadCloud"
                              disabled={true}
                            />,
                          ],
                        },
                      ]}
                    />
                  </div>
                </div>
              </div>

              <div className="w-full flex-col gap-6 items-start flex">
                {showSsoBanner && (
                  <Alert
                    variant="warning"
                    title={
                      'Setup SSO Integration to reduce management overhead!'
                    }
                    description={
                      'Admins can securely deploy Chkk to their organizations with SSO. Employees use their existing ' +
                      'corporate login when accessing the Chkk Dashboard, removing the need for further credential management. ' +
                      'This includes provisioning and deprovisioning of accounts as people join and leave your organization. ' +
                      'Chkk supports SAML and OIDC. Click on this banner to get started with setting up the integration.'
                    }
                    className="cursor-pointer"
                    onClick={() => {
                      logEvent('teams-sso-banner-click');
                      enqueueSnackbar(
                        "Thank you for the interest in getting SSO setup. A member of Chkk's support team will be in touch soon.",
                        {
                          variant: 'success',
                        },
                      );
                    }}
                  />
                )}
                <div className="w-full justify-between items-center flex">
                  <Tabs
                    id="team-details-tabs"
                    className="h-auto w-auto flex-none"
                  >
                    {TEAM_TABS.map((tab) => (
                      <Tabs.Item
                        id={tab.id}
                        key={tab.key}
                        active={tab.key === currentTab}
                        onClick={() => {
                          logEvent(tab.trackerEvent);
                          setCurrentTab(tab.key as TabTypes);
                        }}
                        icon={tab.icon as SubframeCore.IconName}
                      >
                        {isCurrentTabLoading ? (
                          <SkeletonText className="h-6 w-[100px]" />
                        ) : (
                          tab.title
                        )}
                      </Tabs.Item>
                    ))}
                  </Tabs>
                  <div className="gap-2 flex"></div>
                </div>
                <div className="w-full flex-col gap-3 items-start flex">
                  {TEAM_TABS.map((tab) => {
                    const isMatched = tab.key === currentTab;
                    return (
                      isMatched && (
                        <>
                          {isCurrentTabLoading ? (
                            <div className="flex self-center">
                              <CircularProgress />
                            </div>
                          ) : (
                            tab.component
                          )}
                        </>
                      )
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

export default Teams;
